import { useEffect, useState } from 'react'
import { Lock, Sparkles } from 'lucide-react'
import { advancedApi, type UsageResponse } from '../api'
import UpgradeModal from './UpgradeModal'

interface SubscriptionNoticeProps {
  context?: 'dashboard' | 'upload' | 'processing' | 'preview' | 'export' | 'download'
  className?: string
  force?: boolean
}

const copy = {
  dashboard: {
    title: 'Free limited account',
    body: 'Your free account includes 3 PDF uploads per month, 1 processed page per document, 1 export per document, and no advanced scan access.',
  },
  upload: {
    title: 'Free upload limit',
    body: 'Free accounts can upload 3 PDFs per month. Upgrade when you need unlimited documents and full-page processing.',
  },
  processing: {
    title: 'Processing in free test mode',
    body: 'This run extracts the first page only. Upgrade to process every page in the PDF and unlock advanced scans.',
  },
  preview: {
    title: 'Preview from the free version',
    body: 'The preview reflects the free one-page extraction limit. Upgrade to process all pages before selecting columns and exporting.',
  },
  export: {
    title: 'Free export limit',
    body: 'Free accounts include 1 export per document. Upgrade for unlimited exports, full document processing, and advanced scans.',
  },
  download: {
    title: 'Download from free version',
    body: 'This export was created with free-plan limits. Upgrade to export full processed documents without the one-export limit.',
  },
}

export default function SubscriptionNotice({ context = 'dashboard', className = '', force = false }: SubscriptionNoticeProps) {
  const [usage, setUsage] = useState<UsageResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [upgradeOpen, setUpgradeOpen] = useState(false)

  useEffect(() => {
    advancedApi.getUsage()
      .then((res) => setUsage(res.data))
      .catch(() => setUsage(null))
      .finally(() => setLoading(false))
  }, [])

  if (loading && !force) return null
  if (!force && usage?.plan_type && usage.plan_type !== 'free') return null
  if (!force && !usage) return null

  const message = copy[context]

  return (
    <>
      <div className={`rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-950 ${className}`}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3">
            <Lock className="mt-0.5 h-5 w-5 text-amber-700" />
            <div>
              <h3 className="font-semibold">{message.title}</h3>
              <p className="mt-1 text-sm text-amber-800">{message.body}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setUpgradeOpen(true)}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-gray-950 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-gray-800"
          >
            <Sparkles className="h-4 w-4" />
            Upgrade
          </button>
        </div>
      </div>
      <UpgradeModal
        isOpen={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        currentPlan={usage?.plan_type ?? 'free'}
      />
    </>
  )
}
